import { type NoRefs, type DeepWriteable } from "types/utils";

import { type UpsertNotificationRuleStepsContainer_notificationRule$data } from "../components/__generated__/UpsertNotificationRuleStepsContainer_notificationRule.graphql";
import { notificationRuleFilterJSONToCriteriaFilters } from "./notificationRuleFilterToCriteriaFilters";

import type { JiraInstanceData } from "../components/dropdowns/JiraInstance";
import type {
  JiraAssigneeValue,
  RulesFilterFormData,
} from "../components/schema";

type MinimalJiraInstance = Pick<
  JiraInstanceData,
  "uuid" | "url" | "username" | "spaces" | "defaultSpace" | "defaultWorkType"
>;

function mapJiraInstance(
  uuid: string | null | undefined,
): MinimalJiraInstance | null {
  if (!uuid) return null;

  // The NotificationRule fragment only gives us the UUID.
  // We’ll hydrate this into a full JiraInstanceData object once the Jira instances
  // list query has loaded (see JiraInstance's initial selection hydration).
  return {
    uuid,
    url: "",
    username: "",
    spaces: [],
    defaultSpace: null,
    defaultWorkType: null,
  };
}

function mapJiraSpaceSelections(
  target: UpsertNotificationRuleStepsContainer_notificationRule$data["target"],
): string[] {
  const space = target?.space;
  if (!space) return [];

  // UI currently only supports literal space keys (single-column list).
  if (space.type === "SPACE_KEY") {
    return space.value ? [space.value] : [];
  }

  // If the API returns RESOURCE_TAG / ACCOUNT_TAG / ACCOUNT_VARIABLE,
  // the current UI can’t represent it. Leave empty so the user re-selects.
  return [];
}

export function mapNotificationRuleToFormValues(
  rule:
    | DeepWriteable<
        NoRefs<UpsertNotificationRuleStepsContainer_notificationRule$data>
      >
    | null
    | undefined,
): RulesFilterFormData {
  const criteriaFilters = notificationRuleFilterJSONToCriteriaFilters(
    rule?.filterJSON ?? "",
  );

  const jiraTarget = rule?.target ?? null;

  return {
    name: rule?.name ?? "",
    description: rule?.description ?? null,
    providerFilters: criteriaFilters.providerFilters,
    accountFilters: criteriaFilters.accountFilters,
    resourceFilters: criteriaFilters.resourceFilters,
    policyFilters: criteriaFilters.policyFilters,
    bindingFilters: criteriaFilters.bindingFilters,

    jiraInstance: mapJiraInstance(
      jiraTarget?.instance?.uuid as unknown as string | null,
    ),
    jiraSpace: mapJiraSpaceSelections(jiraTarget),
    jiraWorkType: jiraTarget?.workType ? [jiraTarget.workType] : [],
    jiraGrouping: rule?.groupBy ? [...rule.groupBy] : [],
    jiraAssignee: jiraTarget?.assignee
      ? ({
          type: jiraTarget.assignee.type,
          value: jiraTarget.assignee.value ?? null,
        } as JiraAssigneeValue)
      : null,
    jiraIfIssueClosed: [],

    includeCostDataByDefault: rule?.includeCostData ?? false,
    createNewIssuesForResources: rule ? Boolean(rule.updateSchedule) : true,
    issueDescription: jiraTarget?.description ?? null,
  };
}
